import {useEffect, useState} from 'react'
import {
    filterToState,
    getItemId,
    getTableData,
    mergeOldAndNewFilters,
    setVideoUrlFilters,
    sortToState,
    stateToSort,
} from 'services/utils/tables'
import {INITIAL_PAGE_SIZE} from 'constants/tables'
import {useI18n} from 'store/Store'
import {useNavigate} from 'react-router-dom'
import {useUrlParams} from 'services/hooks'
import dayjs from 'services/utils/dayjs'
import {formatItemByLocale} from 'services/utils/data'
import {getLocale} from 'localization/i18n'

const useTable = ({
    itemName,
    useGetQuery,
    paginationType,
    useDeleteMutation,
    visibleKeys,
    allowExpand,
    onEdit,
    onCreate,
    translationKey,
    confirmBeforeDelete,
}) => {
    const locale = getLocale()
    const {i18n} = useI18n()
    const navigate = useNavigate()

    const tableName = `${itemName}s`

    const {urlParams, setUrlParam} = useUrlParams()
    const [visibleColumns, setVisibleColumns] = useState()
    const [itemToDelete, setItemToDelete] = useState()

    const tablePrefixKeys = Object.keys(urlParams).filter(
        (key) =>
            key.endsWith('_page') ||
            key.endsWith('_pageSize') ||
            key.endsWith('_sort') ||
            key.endsWith('_search')
    )

    const {
        [`${itemName}_search`]: search,
        [`${itemName}_page`]: urlPage,
        [`${itemName}_pageSize`]: urlPageSize,
        [`${itemName}_sort`]: sort,
        ...restParams
    } = urlParams

    const filter = Object.fromEntries(
        Object.entries(restParams).filter(
            ([key]) => !tablePrefixKeys.includes(key)
        )
    )

    const page = +(urlPage || 0)
    const pageSize = +(urlPageSize || INITIAL_PAGE_SIZE)
    const initialPagination = {pageIndex: page, pageSize}

    const handlePagination = (updater) => {
        if (updater instanceof Function) {
            const newPagination = updater(initialPagination)
            setUrlParam(`${itemName}_page`, newPagination.pageIndex)
            setUrlParam(`${itemName}_pageSize`, newPagination.pageSize)
        }
    }

    const sorting = sortToState(sort)

    const onSortingChange = (updater) => {
        if (updater instanceof Function) {
            const newSorting = updater(sorting)
            setUrlParam(`${itemName}_sort`, stateToSort(newSorting))
        }
    }

    const handleTableSearch = (searchValue) => {
        setUrlParam(`${itemName}_search`, searchValue)
    }

    const handleColumnFilters = (updater) => {
        if (updater instanceof Function) {
            const newColumnFilters = updater(filterToState(filter))

            const oldFilters = mergeOldAndNewFilters(filter, newColumnFilters)
            const combinedFilters = [...oldFilters, ...newColumnFilters]

            combinedFilters.forEach(({id, value}) => {
                if (dayjs.isDayjs(value)) {
                    value = value.format('YYYY-MM-DD')
                }
                if (id === 'files') {
                    setVideoUrlFilters(value, setUrlParam)
                } else {
                    setUrlParam(id, value)
                }
            })
        }
    }

    const {data, refetch, isFetching} = useGetQuery(
        paginationType === 'external'
            ? {
                  page: page || 0,
                  pageSize: pageSize || INITIAL_PAGE_SIZE,
                  search,
                  sort,
                  ...filter,
              }
            : {}
    )

    useEffect(() => {
        if (paginationType === 'external') {
            refetch()
        }
    }, [refetch, paginationType, sort])

    const [errorMessage, setErrorMessage] = useState(null)
    const [deleteErrors, setDeleteErrors] = useState([])
    const [showErrorModal, setShowErrorModal] = useState(false)
    const [doDelete, {error}] = useDeleteMutation()

    useEffect(() => {
        if (error?.data) {
            if (Array.isArray(error.data.message)) {
                setDeleteErrors(error.data.message)
                setShowErrorModal(true)
            } else {
                setErrorMessage(error.data.message)
            }
        }
    }, [error])

    const handleDelete = (row) => {
        if (confirmBeforeDelete) {
            setItemToDelete(row)
            return
        }

        doDelete(row.id)
    }

    const closeDeleteConfirmation = () => {
        setItemToDelete()
    }

    const closeErrorModal = () => {
        setShowErrorModal(false)
        setDeleteErrors([])
    }

    const getItemToDeleteName = () => {
        const fields =
            itemName === 'publication'
                ? {he: 'detailsHeb', en: 'detailsEng'}
                : {}
        const name = formatItemByLocale(itemToDelete?.original, locale, fields)

        return name || ''
    }

    const {tableKeys, tableData, keyTypes, tableItems, totalItems, columns} =
        getTableData(
            tableName,
            visibleKeys,
            data,
            page,
            pageSize,
            search,
            sort,
            paginationType,
            i18n,
            translationKey
        )

    const [expandedId, setExpandedId] = useState(null)

    const handleExpand = () => (allowExpand ? setExpandedId : null)

    const getExpandedItem = () =>
        tableItems.find((item) => getItemId(item) === expandedId)

    const editOrCreateItem = (id) => {
        if (onEdit) return onEdit(id)
        if (onCreate) return onCreate()

        const itemId = id || 'new'
        navigate(`/${tableName}/${itemId}`)
    }

    const setColumnVisibility = (updater) => {
        if (updater instanceof Function) {
            const newColumns = updater(visibleColumns)
            setVisibleColumns(newColumns)
            localStorage.setItem('columns', JSON.stringify(newColumns))
        }
    }

    useEffect(() => {
        if (!visibleColumns) {
            const columnsVisibility = localStorage.getItem('columns')
            columnsVisibility &&
                setVisibleColumns(JSON.parse(columnsVisibility))
        }
    }, [visibleColumns])

    return {
        tableName,
        tableKeys,
        tableData,
        keyTypes,
        tableItems,
        totalItems,
        errorMessage,
        doDelete,
        expandedId,
        handleExpand,
        getExpandedItem,
        editOrCreateItem,
        columns,
        isLoading: isFetching,
        pagination: initialPagination,
        setPagination: handlePagination,
        sorting,
        setSorting: onSortingChange,
        columnFilters: filterToState(filter),
        setColumnFilters: handleColumnFilters,
        tableSearch: search,
        setTableSearch: handleTableSearch,
        columnVisibility: visibleColumns,
        setColumnVisibility,
        handleDelete,
        itemToDeleteId: itemToDelete?.id,
        itemToDeleteName: getItemToDeleteName(),
        closeDeleteConfirmation,
        showErrorModal,
        deleteErrors,
        closeErrorModal,
    }
}

export default useTable
